/**
 * Dinero en unidad minima entera (centavos). NUNCA punto flotante binario.
 *
 * Un `Money` es un entero seguro de JavaScript + el codigo ISO de la moneda.
 * La base de datos guarda exactamente estos dos campos (`*_minor` INTEGER + `currency` TEXT).
 */

import { assertDomain } from './errors.js';

export interface Money {
  /** Importe en unidad minima (centavos para GTQ/USD). Entero, puede ser negativo. */
  readonly minor: number;
  /** Codigo ISO 4217, 3 letras mayusculas. */
  readonly currency: string;
}

/** Monedas soportadas y sus decimales. Ampliable sin tocar la logica. */
const CURRENCY_DECIMALS: Readonly<Record<string, number>> = Object.freeze({
  GTQ: 2,
  USD: 2,
});

export const DEFAULT_CURRENCY = 'GTQ';

export function currencyDecimals(currency: string): number {
  const decimals = CURRENCY_DECIMALS[currency];
  assertDomain(decimals !== undefined, 'MONEDA_INVALIDA', `Moneda no soportada: ${currency}`, {
    currency,
  });
  return decimals;
}

export function isSupportedCurrency(currency: string): boolean {
  return Object.prototype.hasOwnProperty.call(CURRENCY_DECIMALS, currency);
}

export function money(minor: number, currency: string = DEFAULT_CURRENCY): Money {
  currencyDecimals(currency);
  assertDomain(
    Number.isSafeInteger(minor),
    'MONTO_INVALIDO',
    'El importe debe ser un entero seguro en unidad minima',
    { minor },
  );
  // `-0` se normaliza a `0`: `negate(zero())` lo producia, y aunque no cambia
  // ninguna cifra impresa, `Object.is(-0, 0)` es false y cualquier comparacion
  // estricta con cero fallaria sin motivo visible. Se ataja en el unico
  // constructor para que ninguna operacion pueda dejarlo escapar.
  return Object.freeze({ minor: minor === 0 ? 0 : minor, currency });
}

export function zero(currency: string = DEFAULT_CURRENCY): Money {
  return money(0, currency);
}

export function isZero(a: Money): boolean {
  return a.minor === 0;
}

export function isPositive(a: Money): boolean {
  return a.minor > 0;
}

export function isNegative(a: Money): boolean {
  return a.minor < 0;
}

function sameCurrency(a: Money, b: Money): void {
  assertDomain(
    a.currency === b.currency,
    'MONEDA_INCONSISTENTE',
    `No se pueden operar importes de monedas distintas (${a.currency} vs ${b.currency})`,
    { left: a.currency, right: b.currency },
  );
}

export function add(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return money(a.minor + b.minor, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return money(a.minor - b.minor, a.currency);
}

export function negate(a: Money): Money {
  return money(-a.minor, a.currency);
}

export function sum(items: readonly Money[], currency: string = DEFAULT_CURRENCY): Money {
  return items.reduce<Money>((acc, item) => add(acc, item), zero(currency));
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  sameCurrency(a, b);
  if (a.minor < b.minor) return -1;
  if (a.minor > b.minor) return 1;
  return 0;
}

export function equals(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.minor === b.minor;
}

export function min(a: Money, b: Money): Money {
  return compare(a, b) <= 0 ? a : b;
}

export function max(a: Money, b: Money): Money {
  return compare(a, b) >= 0 ? a : b;
}

/**
 * Multiplica por un factor decimal (p.ej. 0.15 de mora) redondeando half-up
 * sobre la unidad minima. Se usa solo para reglas configurables (descuentos,
 * moras); los importes capturados por el usuario nunca pasan por aqui.
 */
export function multiply(a: Money, factor: number): Money {
  assertDomain(Number.isFinite(factor), 'MONTO_INVALIDO', 'Factor invalido', { factor });
  const raw = a.minor * factor;
  const rounded = raw < 0 ? -Math.round(-raw) : Math.round(raw);
  return money(rounded, a.currency);
}

/**
 * Reparte un importe en N partes lo mas iguales posible sin perder ni inventar
 * centavos: los centavos sobrantes se asignan a las primeras partes.
 */
export function allocate(a: Money, parts: number): Money[] {
  assertDomain(
    Number.isSafeInteger(parts) && parts > 0,
    'MONTO_INVALIDO',
    'El numero de partes debe ser un entero positivo',
    { parts },
  );
  const sign = a.minor < 0 ? -1 : 1;
  const abs = Math.abs(a.minor);
  const base = Math.floor(abs / parts);
  let remainder = abs - base * parts;
  const result: Money[] = [];
  for (let i = 0; i < parts; i += 1) {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    result.push(money(sign * (base + extra), a.currency));
  }
  return result;
}

const AMOUNT_PATTERN = /^-?\d{1,15}(?:[.,]\d{1,6})?$/;

/**
 * Convierte texto capturado por el usuario a `Money`.
 * Acepta "1234.56", "1234,56", " 1,234.56 " (separador de miles) y rechaza todo lo demas.
 * No usa `parseFloat`: el redondeo se hace sobre los digitos, no sobre un binario.
 */
export function parseAmount(input: string, currency: string = DEFAULT_CURRENCY): Money {
  const decimals = currencyDecimals(currency);
  const raw = String(input ?? '').trim();
  assertDomain(raw.length > 0, 'MONTO_INVALIDO', 'El importe es obligatorio', { input });

  // Quita separadores de miles SOLO si el formato es inequivoco.
  //
  // El punto como separador de miles es ambiguo: "10.999" puede ser diez mil
  // novecientos noventa y nueve o diez con 999 milesimas. Por eso el punto solo se
  // trata como separador de miles cuando hay coma decimal ("1.234,56") o al menos
  // dos grupos ("1.234.567"). En cualquier otro caso "10.999" cae en la validacion
  // de decimales y se rechaza, que es el comportamiento seguro para dinero.
  let normalized = raw.replace(/\s/g, '');
  if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(normalized)) {
    normalized = normalized.replace(/,/g, '');
  } else if (
    /^-?\d{1,3}(\.\d{3})+,\d+$/.test(normalized) ||
    /^-?\d{1,3}(\.\d{3}){2,}$/.test(normalized)
  ) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  }

  assertDomain(AMOUNT_PATTERN.test(normalized), 'MONTO_INVALIDO', 'Formato de importe invalido', {
    input,
  });

  const negative = normalized.startsWith('-');
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [intPart = '0', fracRaw = ''] = unsigned.replace(',', '.').split('.');
  assertDomain(
    fracRaw.length <= decimals,
    'MONTO_INVALIDO',
    `La moneda ${currency} admite como maximo ${decimals} decimales`,
    { input, decimals },
  );
  const frac = fracRaw.padEnd(decimals, '0');
  const minorString = `${intPart}${frac}`;
  const minorValue = Number(minorString);
  assertDomain(Number.isSafeInteger(minorValue), 'MONTO_INVALIDO', 'Importe fuera de rango', {
    input,
  });
  return money(negative ? -minorValue : minorValue, currency);
}

/** Igual que `parseAmount` pero exige un importe estrictamente mayor que cero. */
export function parsePositiveAmount(input: string, currency: string = DEFAULT_CURRENCY): Money {
  const value = parseAmount(input, currency);
  assertDomain(value.minor > 0, 'MONTO_NO_POSITIVO', 'El importe debe ser mayor que cero', {
    input,
  });
  return value;
}

/** Representacion plana sin simbolo, apta para Excel/CSV: "1234.56". */
export function toDecimalString(a: Money): string {
  const decimals = currencyDecimals(a.currency);
  const negative = a.minor < 0;
  const digits = String(Math.abs(a.minor)).padStart(decimals + 1, '0');
  const intPart = digits.slice(0, digits.length - decimals);
  const fracPart = digits.slice(digits.length - decimals);
  const body = decimals === 0 ? intPart : `${intPart}.${fracPart}`;
  return negative ? `-${body}` : body;
}

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = Object.freeze({
  GTQ: 'Q',
  USD: '$',
});

/**
 * Formato para pantalla, PDF y Excel. Determinista (no depende del locale del
 * navegador ni del sistema), para que la misma cifra se vea igual en los tres.
 */
export function formatMoney(a: Money, options: { withSymbol?: boolean } = {}): string {
  const { withSymbol = true } = options;
  const decimals = currencyDecimals(a.currency);
  const negative = a.minor < 0;
  const digits = String(Math.abs(a.minor)).padStart(decimals + 1, '0');
  const intPart = digits.slice(0, digits.length - decimals);
  const fracPart = digits.slice(digits.length - decimals);
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = decimals === 0 ? grouped : `${grouped}.${fracPart}`;
  const symbol = withSymbol ? (CURRENCY_SYMBOLS[a.currency] ?? `${a.currency} `) : '';
  return `${negative ? '-' : ''}${symbol}${body}`;
}

/** Reconstruye un `Money` desde las dos columnas persistidas. */
export function fromRow(minor: number | bigint, currency: string): Money {
  return money(typeof minor === 'bigint' ? Number(minor) : minor, currency);
}
